import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { loadStateForPIXI } from "../../utils/pixiHelper.js";

const initialState = {
  canvases: {
    editMockupCanvasState: { undoStack: [], redoStack: [], currentState: null },
    editSVGCanvasState: { undoStack: [], redoStack: [], currentState: null },
    previewMockupCanvasState: { undoStack: [], redoStack: [], currentState: null },
  },
  zoom: 1,
};


 
export const undoAndReload = createAsyncThunk(
  "canvasesstate/undoAndReload",
  async (tab, { dispatch, getState }) => {
    // Perform the undo operation on Redux state.
    dispatch(undo({ tab }));

    const state = getState();
    const currentCanvasState = state.canvasesstate.canvases[tab]?.currentState;
    if (!currentCanvasState) {
      console.log("Undo: currentCanvasState is missing for", tab);
      return;
    }
 
    // If the tab is for preview (PIXI), update the PIXI canvas.
    if (tab === "previewMockupCanvasState") {
      const pixiCanvas = state.canvas?.app;
      if (pixiCanvas) {
        console.log("Undo: Loading PIXI canvas for", tab);
        pixiCanvas.__isLoadingState = true;
        await new Promise((resolve) => {
          // Call loadStateForPIXI with the correct parameter order:
          // (pixiCanvas, stateString, callback)
          loadStateForPIXI(pixiCanvas, currentCanvasState, () => {
            pixiCanvas.renderer.render(pixiCanvas.stage);
            window.dispatchEvent(new CustomEvent("pixiControlsHide"));
            pixiCanvas.__isLoadingState = false;
            console.log("PIXI canvas updated after undo");
            resolve();
          });
        });
      } else {
        console.log("Undo: PIXI canvas missing for", tab);
      }
    }
    // Otherwise, assume Fabric canvas for editing tabs.
    else if (tab === "editSVGCanvasState" || tab === "editMockupCanvasState") {
      const fabricCanvas = state.fabricCanvas?.fabricCanvas;
      if (fabricCanvas) {
        console.log("Undo: Loading Fabric canvas for", tab);
        fabricCanvas.__isLoadingState = true;
        await new Promise((resolve) => {
          fabricCanvas.loadFromJSON(currentCanvasState, () => {
            
            fabricCanvas.renderAll();
            fabricCanvas.__isLoadingState = false;
            console.log("Fabric canvas updated after undo");
            resolve();
          });
        });
      } else {
        console.log("Undo: Fabric canvas missing for", tab);
      }
    } else {
      console.log("Undo: Unknown tab", tab);
    }
  }
);

// Thunk for Redo and Reload
export const redoAndReload = createAsyncThunk(
  "canvasesstate/redoAndReload",
  async (tab, { dispatch, getState }) => {
    // Perform the redo operation on Redux state.
    dispatch(redo({ tab }));

    const state = getState();
    const currentCanvasState = state.canvasesstate.canvases[tab]?.currentState;
    if (!currentCanvasState) {
      console.log("Redo: currentCanvasState is missing for", tab);
      return;
    }

    if (tab === "previewMockupCanvasState") {
      const pixiCanvas = state.canvas?.app;
      if (pixiCanvas) {
        console.log("Redo: Loading PIXI canvas for", tab);
        pixiCanvas.__isLoadingState = true;
        await new Promise((resolve) => {
          loadStateForPIXI(pixiCanvas, currentCanvasState, () => {
            pixiCanvas.renderer.render(pixiCanvas.stage);
            // window.dispatchEvent(new CustomEvent("pixiControlsHide"));
            pixiCanvas.__isLoadingState = false;
            console.log("PIXI canvas updated after redo");
            resolve();
          });
        });
      } else {
        console.log("Redo: PIXI canvas missing for", tab);
      }
    } else if (tab === "editSVGCanvasState" || tab === "editMockupCanvasState") {
      const fabricCanvas = state.fabricCanvas?.fabricCanvas;
      if (fabricCanvas) {
        console.log("Redo: Loading Fabric canvas for", tab);
        fabricCanvas.__isLoadingState = true;
        await new Promise((resolve) => {
          fabricCanvas.loadFromJSON(currentCanvasState, () => {
            fabricCanvas.renderAll();
            fabricCanvas.__isLoadingState = false;
            console.log("Fabric canvas updated after redo");
            resolve();
          });
        });
      } else {
        console.log("Redo: Fabric canvas missing for", tab);
      }
    } else {
      console.log("Redo: Unknown tab", tab);
    }
  }
);

const canvasSlice = createSlice({
  name: "canvasesstate",
  initialState,
  reducers: {
    saveState: (state, action) => {
      const { tab, canvasState } = action.payload;
      if (state.canvases[tab]) {
        state.canvases[tab].undoStack.push(canvasState);
        state.canvases[tab].redoStack = []; 
        state.canvases[tab].currentState = canvasState;
        console.log("saveState:", { tab, canvasState, currentCanvas: state.canvases[tab] });
      } else {
        console.log("saveState failed: No canvas found for tab", tab);
      }
    },
    setZoom: (state, action) => {
      state.zoom = action.payload;
      console.trace("setZoom:", action.payload);
    },
    undo: (state, action) => {
      const { tab } = action.payload;
      const canvas = state.canvases[tab];
      if (canvas && canvas.undoStack.length > 0) {
        const lastState = canvas.undoStack.pop();
        canvas.redoStack.push(lastState);
        canvas.currentState =
          canvas.undoStack[canvas.undoStack.length - 1] || null;
        console.log(`undo: For tab ${tab}, removed state:`, lastState);
      } else {
        console.log(
          `undo: For tab ${tab} failed (empty undoStack or invalid canvas)`,
          canvas
        );
      }
    },
    redo: (state, action) => {
      const { tab } = action.payload;
      const canvas = state.canvases[tab];
      if (canvas && canvas.redoStack.length > 0) {
        const nextState = canvas.redoStack.pop();
        canvas.undoStack.push(nextState);
        canvas.currentState = nextState;
        console.log(`redo: For tab ${tab}, restored state:`, nextState);
      } else {
        console.log(
          `redo: For tab ${tab} failed (empty redoStack or invalid canvas)`,
          canvas
        );
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(undoAndReload.fulfilled, (state, action) => {
        // No additional state update required.
      })
      .addCase(redoAndReload.fulfilled, (state, action) => {
        // No additional state update required.
      });
  },
});

export const { saveState, setZoom, undo, redo } = canvasSlice.actions;
export default canvasSlice.reducer;






